// The handful of object helpers the forms and stores need, in place of lodash.
// They work on the shapes those code paths hold: plain objects, arrays, dates
// and primitives, often behind a Vue reactive proxy. That proxy is why
// `structuredClone` is not used: it throws on one.

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

// A deep copy of plain objects, arrays and dates. Anything else (a class
// instance, a function, a File) is shared rather than copied.
export function cloneDeep<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => cloneDeep(item)) as T;
  if (value instanceof Date) return new Date(value.getTime()) as T;
  if (isPlainObject(value)) {
    const copy: Record<string, unknown> = {};
    for (const key of Object.keys(value)) copy[key] = cloneDeep(value[key]);
    return copy as T;
  }
  return value;
}

// Whether two values hold the same data: plain objects key by key (order
// ignored), arrays item by item, dates by time, everything else by `Object.is`.
export function isEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => isEqual(item, b[i]));
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && isEqual(a[key], b[key]));
  }
  return false;
}

// "UserIdPs[2].DisplayName" -> ["UserIdPs", "2", "DisplayName"]. A form field's
// name is a path written this way.
function pathSegments(path: string | readonly (string | number)[]): string[] {
  if (Array.isArray(path)) return path.map(String);
  return String(path)
    .replace(/\[(\w+)\]/g, ".$1")
    .split(".")
    .filter((segment) => segment !== "");
}

// The value at `path`, or undefined where any step along it is missing.
export function getPath(object: unknown, path: string | readonly (string | number)[]): unknown {
  let current: unknown = object;
  for (const segment of pathSegments(path)) {
    if (current === null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

// Writes `value` at `path`, creating the objects and arrays on the way (an
// array where the next step is a number). Returns the object it was given.
export function setPath<T extends object>(object: T, path: string | readonly (string | number)[], value: unknown): T {
  const segments = pathSegments(path);
  // A path naming the prototype would write onto every object in the page.
  if (segments.some((s) => s === "__proto__" || s === "constructor" || s === "prototype")) return object;
  let current = object as Record<string, unknown>;
  segments.forEach((segment, i) => {
    if (i === segments.length - 1) {
      current[segment] = value;
      return;
    }
    const next = current[segment];
    if (next === null || typeof next !== "object") {
      current[segment] = /^\d+$/.test(segments[i + 1]) ? [] : {};
    }
    current = current[segment] as Record<string, unknown>;
  });
  return object;
}
